/**
 * NeedsYouLane — the triage strip above the pipeline (PR4-fidelity). Computed
 * by the board from the intakes (overdue → "Send"; ready-for-assignment with no
 * owning CM → "Assign care manager"). The "Send" CTA opens the drawer; the
 * "Assign care manager" CTA (action:'assign') opens the Assign form directly
 * (Slice 2 — the Assigned column was removed, so the drag-to-assign path is gone).
 */

import { type ReactNode } from 'react';

export interface NeedsYouItem {
  id: string;
  name: string;
  note: string;
  cta: string;
  /** 'assign' → open the Assign-care-manager form; else (default) open the drawer. */
  action?: 'open' | 'assign';
}

export interface NeedsYouLaneProps {
  items: NeedsYouItem[];
  summary: string;
  onOpen: (id: string) => void;
  onAssign?: (id: string) => void;
}

export function NeedsYouLane({ items, summary, onOpen, onAssign }: NeedsYouLaneProps): ReactNode {
  if (items.length === 0) return null;
  return (
    <div
      data-testid="needs-you-lane"
      className="bg-paper border border-warm-200 rounded-card px-4 pt-3.5 pb-4 mb-5 shadow-sh2"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <span className="w-2 h-2 rounded-pill bg-warm-400" aria-hidden="true" />
        <span className="text-[13px] font-bold text-sage-800">Needs you</span>
        <span className="text-xs text-sage-400">{summary}</span>
      </div>
      <div className="flex gap-3 flex-wrap">
        {items.map((n) => (
          <div
            key={n.id}
            data-testid="needs-you-item"
            className="flex-1 min-w-[230px] flex items-center gap-3 bg-cream rounded-xl px-3 py-2.5"
          >
            <span aria-hidden="true" className="inline-flex items-center justify-center w-[34px] h-[34px] rounded-pill bg-cream text-sage-400 border border-dashed border-sage-300 text-xs font-semibold flex-shrink-0">
              ?
            </span>
            <div className="min-w-0">
              <div className="font-display font-semibold text-[14.5px] text-sage-800 leading-tight">{n.name}</div>
              <div className="text-xs text-warm-700 mt-0.5">{n.note}</div>
            </div>
            <button
              type="button"
              data-testid="needs-you-cta"
              onClick={() => (n.action === 'assign' && onAssign ? onAssign(n.id) : onOpen(n.id))}
              aria-label={`${n.cta}: ${n.name}`}
              className="ml-auto bg-sage-800 text-white rounded-[7px] px-3 py-1.5 text-xs font-semibold whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              {n.cta}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
